// =============================================================================
// components/glass/tokens.ts — Glass Contract tokens (RN translation of --glass-*)
//
// Single source of truth for CRYB's lavender glassmorphism. Every glass primitive
// reads from here; feature agents must NOT hardcode glass values. Keeps the brand:
// lavender surfaces (#E8E1F2 family) + primary #6C5CE7. No new hues.
//
// RN note: there is no CSS backdrop-filter without a native blur module, so "glass"
// here is built from a translucent surface + a top sheen highlight + a low-alpha
// border + a tuned ambient shadow, over the app's BackgroundBlobs. This reads as
// real glass on the lavender bg and ships with zero native rebuild.
// =============================================================================
import { colors, shadowTokens } from '../../constants/theme';

export type GlassVariant = 'card' | 'bar' | 'sheet' | 'pill';

export const glassTokens = {
  // Translucent fills per surface type (more opaque = floats higher).
  // OVERHAUL: crisper, higher-opacity surfaces so cards read as solid PREMIUM
  // material over the busy particle bg (was murky/low-contrast at 0.55) — sharper,
  // more "product", less "soft lavender bubble".
  surface: {
    card: 'rgba(255,255,255,0.74)',
    bar: 'rgba(255,255,255,0.82)',
    sheet: 'rgba(255,255,255,0.88)',
    pill: 'rgba(255,255,255,0.66)',
    pillActive: colors.primary,        // active filter/chip
  } as Record<string, string>,

  // Edges + light. Crisper hairline (was a fat white 0.85 halo → softened edges).
  border: 'rgba(255,255,255,0.7)',
  borderSubtle: 'rgba(108,92,231,0.14)',
  highlightTop: 'rgba(255,255,255,0.6)',   // top inner sheen (fake light edge)
  highlightTo: 'rgba(255,255,255,0)',

  // Shadow (RN supports one shadow/View; this is the ambient. Contact edge is the
  // border). Tuned to the brand purple so cards lift off lavender, not grey.
  //
  // BACKWARD-COMPAT: the flat color/radius/offset/opacity/elevation keys below are
  // the original ambient shadow read by GlassSurface — kept unchanged. The nested
  // shadow.{card,bar,sheet} keys (added P0-A) mirror constants/theme shadowTokens
  // so elevation has a single source; use those in new code.
  shadow: {
    // OVERHAUL: tighter, more DEFINED lift (was a soft 16px purple haze) — reads
    // sharp/premium instead of blurry.
    color: 'rgba(108,92,231,0.13)',
    radius: 12,
    offset: { width: 0, height: 4 },
    opacity: 1,
    elevation: 5,
    // per-surface elevation presets (P0-A) — full RN shadow style objects.
    card: shadowTokens.card,
    bar: shadowTokens.bar,
    sheet: shadowTokens.sheet,
  },

  radius: { sm: 12, md: 16, lg: 20, xl: 26, pill: 9999 },

  // Interaction.
  pressScale: 0.97,
  disabledOpacity: 0.45,
} as const;

// Dark-mode glass surfaces (voice/call). Additive — light glass stays the default
// in glassTokens.surface above; dark consumers opt in via these tokens.
export const glassColorTokens = {
  dark: {
    surface: {
      card: 'rgba(26,26,46,0.55)',
      bar: 'rgba(26,26,46,0.74)',
      sheet: 'rgba(26,26,46,0.82)',
      pill: 'rgba(255,255,255,0.10)',
      pillActive: colors.primaryLight,
    } as Record<string, string>,
    border: 'rgba(255,255,255,0.12)',
    borderSubtle: 'rgba(255,255,255,0.08)',
    highlightTop: 'rgba(255,255,255,0.10)',
    highlightTo: 'rgba(255,255,255,0)',
  },
} as const;

export function surfaceFor(variant: GlassVariant, mode: 'light' | 'dark' = 'light'): string {
  const table = mode === 'dark' ? glassColorTokens.dark.surface : glassTokens.surface;
  return table[variant] ?? table.card;
}
